import { Banner } from '@sunwebgroup/uds-ui/banner'
import { mqMin, space } from '@sunwebgroup/ui-components'
import { range } from 'ramda'

import { Fragment, useEffect, useState } from 'react'
import { styled } from 'styled-components'

import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import {
  useRoomsParticipantsDistributionStore,
} from '@/scopes/common/components/Filters/Participants/useParticipantsStore'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { SearchTileProductAds } from '@/scopes/product-ads/components/SearchTile'
import { DEFAULT_RESULTS_PER_PAGE } from '@/scopes/search/search/domain/constants'
import { useFilters, usePagination, useResults } from '@/scopes/search/search/domain/hooks'
import {
  shouldShowProductCardAirportDropdown,
} from '@/scopes/search/search/domain/policies/airportDropdownVisibility'
import {
  ProductCardSplitDetailsLoading,
} from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/ProductCardSplitDetails/ProductCardSplitDetailsLoading'
import { Sorting } from '@/scopes/search/search/pages/SearchPage/components/Results/Sorting'
import { SelectedFiltersTokens } from '@/scopes/search/search/pages/SearchPage/components/SelectedFiltersTokens'
import AccomodationTipsSlider from './AccommodationTipsSlider'
import { CollectionBanner } from './CollectionBanner'
import { EmptyState } from './EmptyState'
import { SearchPagination } from './Pagination'
import { ProductCard, ProductCardSplitDetails } from './ProductCard'
import { ProductCardLoading } from './ProductCard/ProductCardLoading'
import { Result } from './Result'
import { ResultsCounter } from './ResultsCounter'
import { Usps } from './Usps'

const Wrapper = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: 800px){
    ${ProductCardSplitDetails.modSize('tablet')}
    ${ProductCardSplitDetailsLoading.modSize('tablet')}
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${ProductCard.modSize('tablet')}
    ${ProductCardLoading.modSize('tablet')};
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${ProductCard.modSize('desktop')}
    ${ProductCardLoading.modSize('desktop')}
    ${ProductCardSplitDetails.modSize('desktop')}
    ${ProductCardSplitDetailsLoading.modSize('desktop')}
  }

  @media (min-width: 1280px) {
    ${ProductCardSplitDetails.modSize('tablet')}
    ${ProductCardSplitDetailsLoading.modSize('tablet')}
  }

  @media (min-width: 1440px) {
    ${ProductCardSplitDetails.modSize('desktop')}
    ${ProductCardSplitDetailsLoading.modSize('desktop')}
  }

  > * {
    margin-bottom: ${space('primitives_space_16')};
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${space('primitives_space_8')};
`

const Content = styled.div`
  > * {
    margin-bottom: ${space('primitives_space_8')};
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

const MobileOnly = styled.div`
  display: flex;
  flex-wrap: wrap;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    display: none;
  }
`

const ResultsList = styled.div`
  > * {
    margin-bottom: ${space('primitives_space_16')};;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

export function Results() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { resetOptionalFilters } = useFilters()
  const {
    isFetching,
    results,
    totalResults,
    error,
    filteredCollection,
    ga4FilterCollection,
    ga4Impressions,
    accommodationTips,
  } = useResults()
  const contextItemId = useCMSPageField('contextItemId')
  const siteName = useConfiguration('siteName')
  const { isFirstPage } = usePagination()
  const { roomsParticipantDistribution } = useRoomsParticipantsDistributionStore()
  const accoTipsEnabled = useCMSPageField('SearchPage.accoTipsEnabled')

  // Indicates whether the search results are limited when the room participant distribution exceeds 3 people
  const [hasLimitedSearchResults, setHasLimitedSearchResults] = useState(false)
  // Indicates whether the search results are empty, because of an excessive amount of participants in a room
  const [hasExcesiveParticipants, setHasExcesiveParticipants] = useState(false)
  const { productCardSplitDetails } = useOptimizelyFlagContext()

  useEffect(() => {
    // Check if any room in the participant distribution has more than 3 people
    setHasLimitedSearchResults(
      roomsParticipantDistribution.some(room => room.adults + room.children.length + room.babies.length > 3),
    )
    setHasExcesiveParticipants(
      roomsParticipantDistribution.some(room => room.adults + room.children.length + room.babies.length >= 10),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Disabled because we only want to listen to changes in 'results' and not 'roomsParticipantDistribution'
  }, [results])

  useEffect(() => {
    sendEvent({
      event: 'eec.impressionView',
      ecommerce: {
        impressions: ga4Impressions,
      },
    })
  }, [ga4Impressions, sendEvent])

  useEffect(() => {
    sendEvent({
      event: 'filterCollection',
      ...ga4FilterCollection,
    })
  }, [ga4FilterCollection, sendEvent])

  const hasResults = totalResults !== 0
  const renderLister = isFirstPage && totalResults >= DEFAULT_RESULTS_PER_PAGE
  const renderAccommodationTips = accoTipsEnabled && accommodationTips.length > 0 && renderLister
  const renderProductAds = !accoTipsEnabled && renderLister
  const showProductCardAirportDropdown = shouldShowProductCardAirportDropdown(siteName)

  const ProductCardLoadingComponent = productCardSplitDetails ? ProductCardSplitDetailsLoading : ProductCardLoading

  return (
    <Wrapper>
      <Header>
        <ResultsCounter isLoading={isFetching} />
        <Sorting />

      </Header>
      {error && (
        <EmptyState
          title={intl.formatMessage({
            id: 'search_results_error_title',
          })}
          description={intl.formatMessage({
            id: 'search_results_error_description',
          })}
          confirmationText={intl.formatMessage({
            id: 'search_results_error_action_label',
          })}
          onConfirmClick={resetOptionalFilters}
        />
      )}
      {!error && !hasResults && hasExcesiveParticipants && (
        <EmptyState
          title={intl.formatMessage({
            id: 'search_results_no_results_participants_title',
          })}
          description={intl.formatMessage({
            id: 'search_results_no_results_participants_description',
          })}
        />
      )}
      {!error && !hasResults && !hasExcesiveParticipants && (
        <EmptyState
          title={intl.formatMessage({
            id: 'search_results_no_results_title',
          })}
          description={intl.formatMessage({
            id: 'search_results_no_results_description',
          })}
          confirmationText={intl.formatMessage({
            id: 'search_results_no_results_action_label',
          })}
          onConfirmClick={resetOptionalFilters}
        />
      )}
      {hasResults && (
        <>
          {hasLimitedSearchResults && (
            <Banner
              color="warning"
              size="M"
              display="inline"
              supportingText={intl.formatMessage({ id: 'search_page_limited_amount_search_results' })}
              text=""
            />
          )}
          {filteredCollection && (
            <CollectionBanner
              collection={filteredCollection.collection}
              description={filteredCollection.description}
              image={filteredCollection.image}
              title={filteredCollection.title}
              usps={filteredCollection.usps}
            />
          )}
          <Content>
            <MobileOnly>
              <SelectedFiltersTokens withResetButton />
            </MobileOnly>
            <ResultsList>
              {isFetching && range(0, DEFAULT_RESULTS_PER_PAGE).map(index => (
                <ProductCardLoadingComponent key={index} />
              ))}
              {!isFetching
              && results.map((result, index) => (
                <Fragment key={result.deskId}>
                  <Result {...result} showAirportDropdown={showProductCardAirportDropdown} />
                  {renderAccommodationTips && index === 1 && (
                    <AccomodationTipsSlider
                      items={accommodationTips}
                      participants={roomsParticipantDistribution}
                    />
                  )}
                  {renderProductAds && index === 1
                  && (
                    <SearchTileProductAds
                      contextItemId={contextItemId}
                      headerTitle={intl.formatMessage({ id: 'search_result_product_ads_title' })}
                      infoLabel={intl.formatMessage({ id: 'search_result_product_ads_label' })}
                      infoContent={intl.formatMessage({ id: 'search_result_product_ads_content' })}
                    />
                  )}
                  {index % 6 === 1 && index < DEFAULT_RESULTS_PER_PAGE - 2 && <Usps uspVisibleOnMobile={1} />}
                  {index % 6 === 3 && index < DEFAULT_RESULTS_PER_PAGE - 2 && <Usps uspVisibleOnMobile={2} />}
                  {index % 6 === 5 && index < DEFAULT_RESULTS_PER_PAGE - 2 && <Usps uspVisibleOnMobile={3} />}
                </Fragment>
              ))}
            </ResultsList>
          </Content>
          <SearchPagination />
        </>
      )}
    </Wrapper>
  )
}
